import { createFileRoute, Link } from "@tanstack/react-router";
import { Clock, MapPin, Utensils, Waves, Sparkles, Ship, Coffee, Moon, Sun } from "lucide-react";

const URL = "https://whatfinland.com/guides/48-hours-in-helsinki";
const TITLE = "48 Hours in Helsinki: The Perfect 2-Day Itinerary (2026)";
const DESC =
  "A locally-curated 48-hour Helsinki itinerary — Suomenlinna, Allas sauna, Market Square, Design District, top dinners and where to drink in two days.";
const COVER = "https://whatfinland.com/og/helsinki-48h.jpg";

export const Route = createFileRoute("/guides/48-hours-in-helsinki")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: "helsinki itinerary, 48 hours in helsinki, 2 days in helsinki, helsinki guide, helsinki travel guide, what to do in helsinki" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { property: "og:image", content: COVER },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      { name: "twitter:image", content: COVER },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESC,
          image: [COVER],
          datePublished: "2026-06-10",
          dateModified: "2026-06-10",
          author: { "@type": "Organization", name: "what Finland" },
          publisher: {
            "@type": "Organization",
            name: "what Finland",
            logo: { "@type": "ImageObject", url: "https://whatfinland.com/icon-512.png" },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": URL },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "what Finland", item: "https://whatfinland.com/" },
            { "@type": "ListItem", position: 2, name: "Guides", item: "https://whatfinland.com/" },
            { "@type": "ListItem", position: 3, name: "48 Hours in Helsinki", item: URL },
          ],
        }),
      },
    ],

  }),
  component: HelsinkiItineraryPage,
});

type Stop = { time: string; title: string; body: string; icon: React.ComponentType<{ className?: string }>; link?: { to: string; label: string } };

const day1: Stop[] = [
  { time: "8:30", title: "Breakfast at a local café", body: "Start in the Design District with a flat white and cardamom bun. Skip hotel breakfast — Helsinki's café scene is one of its real joys.", icon: Coffee, link: { to: "/eat", label: "Browse cafés" } },
  { time: "10:00", title: "Senate Square & Helsinki Cathedral", body: "Walk the white neoclassical heart of the city, then duck into the National Library next door.", icon: MapPin },
  { time: "12:30", title: "Old Market Hall lunch", body: "Salmon soup, archipelago bread, reindeer sandwich — the classic harbour-side first bite of Helsinki.", icon: Utensils },
  { time: "14:00", title: "Ferry to Suomenlinna", body: "A 15-minute HSL ferry from Market Square to the UNESCO sea fortress. Plan 2–3 hours: tunnels, ramparts, the King's Gate.", icon: Ship },
  { time: "18:00", title: "Allas Sea Pool sauna", body: "Sauna then a plunge into the Baltic with the cathedral on the skyline. The most Helsinki thing you can do.", icon: Waves, link: { to: "/do", label: "Find more saunas" } },
  { time: "20:30", title: "Dinner in Punavuori", body: "Bistro-style modern Finnish — book ahead. Our Eat list has the current locals' picks.", icon: Utensils, link: { to: "/eat", label: "Tonight's best dinners" } },
  { time: "23:00", title: "Nightcap", body: "A cocktail bar in Kallio or a low-key wine bar in Punavuori — Helsinki nightlife is small, friendly and walkable.", icon: Moon },
];

const day2: Stop[] = [
  { time: "9:00", title: "Hakaniemi Market breakfast", body: "Local market hall, less touristed than the south harbour. Pastries, coffee, fresh berries.", icon: Coffee },
  { time: "10:30", title: "Oodi Central Library + Kiasma", body: "Oodi is a free architectural wonder; cross the square to Kiasma for contemporary art.", icon: Sparkles },
  { time: "12:30", title: "Lunch in Kallio", body: "Helsinki's coolest neighborhood — vintage shops, ramen, natural wine. Walk it slowly.", icon: Utensils },
  { time: "15:00", title: "Temppeliaukio Rock Church + Sibelius Monument", body: "Two short tram rides to the city's most photographed spots. Easy to combine in one loop.", icon: MapPin },
  { time: "17:30", title: "Löyly waterfront sauna", body: "Architect-designed wood sauna on the south shore. Book the evening slot and stay for sunset.", icon: Waves, link: { to: "/do", label: "All sauna spots" } },
  { time: "20:00", title: "Final dinner", body: "Splurge on a tasting menu, or share small plates at a neighborhood bistro — both are very Helsinki.", icon: Utensils },
  { time: "22:30", title: "Walk back along the harbour", body: "Past Market Square and the Old Market Hall. In summer the sun barely sets.", icon: Sun },
];

function HelsinkiItineraryPage() {
  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 pt-8 sm:pt-14 pb-24">
      <header className="mb-10 sm:mb-14">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-navy/60 mb-3">Helsinki Guide</p>
        <h1 className="font-heading text-3xl sm:text-5xl leading-tight text-navy mb-4">{TITLE}</h1>
        <p className="text-base sm:text-lg text-navy/75 max-w-2xl">{DESC}</p>
        <div className="mt-6 flex flex-wrap items-center gap-2 text-xs text-navy/60">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-navy/5"><Clock className="size-3.5" /> 2 days</span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-navy/5"><MapPin className="size-3.5" /> Helsinki, Finland</span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-navy/5">Updated June 2026</span>
        </div>
      </header>

      <section className="prose prose-navy max-w-none mb-12">
        <p className="text-navy/80">
          You only have two days in Helsinki and want to spend them well. This itinerary is built from the spots we send friends to: walkable distances, a real sauna, a ferry ride to a UNESCO island, and a couple of dinners worth flying for. Mornings are slow, evenings are long — that's how locals actually do it.
        </p>
      </section>

      <DaySection title="Day 1 — Old Town, Suomenlinna & Sauna" stops={day1} />
      <DaySection title="Day 2 — Kallio, Architecture & Löyly" stops={day2} />

      <section className="mt-14 mb-10">
        <h2 className="font-heading text-2xl sm:text-3xl text-navy mb-4">More Helsinki Reading</h2>
        <ul className="space-y-2 text-navy/80">
          <li>→ <Link to="/guides/best-saunas-helsinki" className="text-royal hover:underline font-semibold">Best Public Saunas in Helsinki</Link> — our deep comparison of Löyly, Allas, Kotiharjun, Kulttuurisauna and Uusi Sauna.</li>
          <li>→ <Link to="/guides/helsinki-neighborhoods" className="text-royal hover:underline font-semibold">Helsinki Neighborhood Guide</Link> — where to stay: Punavuori, Kallio, Töölö and more.</li>
          <li>→ <Link to="/eat" className="text-royal hover:underline font-semibold">Where to Eat in Helsinki</Link> — current locals' picks across every neighborhood.</li>
          <li>→ <Link to="/do" className="text-royal hover:underline font-semibold">Things to Do in Helsinki</Link> — saunas, museums, day-trips and seasonal.</li>
          <li>→ <Link to="/happening" className="text-royal hover:underline font-semibold">What's On This Week</Link> — concerts, exhibitions, pop-ups.</li>
          <li>→ <Link to="/cities" className="text-royal hover:underline font-semibold">Beyond Helsinki</Link> — Turku, Tampere, Hanko, Porvoo.</li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-2xl sm:text-3xl text-navy mb-4">Sources & Useful Links</h2>
        <ul className="space-y-2 text-sm text-navy/70">
          <li>• <a href="https://www.visitfinland.com/en/places-to-go/helsinki-region/" target="_blank" rel="noopener" className="underline hover:text-navy">Visit Finland — Helsinki Region</a> (official tourist board)</li>
          <li>• <a href="https://www.suomenlinna.fi/en/" target="_blank" rel="noopener" className="underline hover:text-navy">Suomenlinna Sea Fortress</a> (UNESCO World Heritage site)</li>
          <li>• <a href="https://www.loylyhelsinki.fi/" target="_blank" rel="noopener" className="underline hover:text-navy">Löyly Helsinki</a> (booking & opening hours)</li>
          <li>• <a href="https://www.allasseapool.fi/en/" target="_blank" rel="noopener" className="underline hover:text-navy">Allas Sea Pool</a> (harbour sauna & pools)</li>
          <li>• <a href="https://www.oodihelsinki.fi/en/" target="_blank" rel="noopener" className="underline hover:text-navy">Oodi Central Library</a> (architecture & events)</li>
          <li>• <a href="https://www.hsl.fi/en" target="_blank" rel="noopener" className="underline hover:text-navy">HSL</a> (tram, metro & Suomenlinna ferry tickets)</li>
        </ul>
      </section>

      <section className="mt-14 rounded-3xl bg-gradient-to-br from-navy via-royal to-[oklch(0.45_0.18_265)] text-white p-6 sm:p-10">
        <h2 className="font-heading text-2xl sm:text-3xl mb-3">Plan the Rest of Your Trip</h2>
        <p className="text-white/80 mb-6 max-w-xl">Live, locally-curated lists of where to eat, what to do and what's on this week.</p>
        <div className="grid grid-cols-3 gap-3">
          <Link to="/eat" className="rounded-2xl bg-white/10 hover:bg-white/20 ring-1 ring-white/15 p-4 text-center font-heading">Eat</Link>
          <Link to="/do" className="rounded-2xl bg-white/10 hover:bg-white/20 ring-1 ring-white/15 p-4 text-center font-heading">Do</Link>
          <Link to="/happening" className="rounded-2xl bg-white/10 hover:bg-white/20 ring-1 ring-white/15 p-4 text-center font-heading">Happening</Link>
        </div>
      </section>
    </article>
  );
}

function DaySection({ title, stops }: { title: string; stops: Stop[] }) {
  return (
    <section className="mb-12">
      <h2 className="font-heading text-2xl sm:text-3xl text-navy mb-6">{title}</h2>
      <ol className="space-y-4">
        {stops.map((s) => {
          const Icon = s.icon;
          return (
            <li key={s.time} className="rounded-2xl bg-white border border-navy/10 p-4 sm:p-5 flex gap-4">
              <div className="shrink-0 inline-grid place-items-center size-11 rounded-xl bg-navy/5 text-navy">
                <Icon className="size-5" />
              </div>
              <div className="min-w-0">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-mono text-[11px] tracking-widest text-navy/55">{s.time}</span>
                  <h3 className="font-heading text-base sm:text-lg text-navy truncate">{s.title}</h3>
                </div>
                <p className="text-sm text-navy/75 leading-relaxed">{s.body}</p>
                {s.link && (
                  <Link to={s.link.to} className="inline-block mt-2 text-xs font-semibold text-royal hover:underline">
                    {s.link.label} →
                  </Link>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
